import React from 'react';
import PropTypes from 'prop-types';
import { colorBorder, colorBackground, colorHover } from '@spark/ui-components/theme';
import { left } from '@spark/ui-components/theme';
import styled from 'styled-components';
import SidebarLink, { SidebarLinkTitle } from './SidebarLink';
import SidebarCategory from './SidebarCategory';
import { useAuth } from '@/context';
import {
  COGNITO_GROUP_ACOMPANHAMENTOS,
  COGNITO_GROUP_BI_PREMIACAO,
  COGNITO_GROUP_CLIENTES,
  COGNITO_GROUP_CONTAINERS,
  COGNITO_GROUP_DEPOSITO,
  COGNITO_GROUP_DESCARGA,
  COGNITO_GROUP_MOTORISTAS,
  COGNITO_GROUP_NOTIFICACOES,
  COGNITO_GROUP_OPERACOES,
  COGNITO_GROUP_PEDIDOS,
  COGNITO_GROUP_PREVISAO_CARGAS,
  COGNITO_GROUP_VEICULOS,
  COGNITO_GROUP_VIAGENS,
} from '@/utils/cognitoGroups';

// Função auxiliar para verificar se um menu está habilitado via variável de ambiente
const isMenuEnabled = (menuId) => {
  // Converte o ID do menu para o formato da variável de ambiente
  // Ex: 'ordensEmissao' -> 'ORDENS_EMISSAO', 'previsaoCargas' -> 'PREVISAO_CARGAS'
  const envKey = `REACT_APP_MENU_${menuId
    .replace(/([A-Z])/g, '_$1')
    .toUpperCase()}`;
  const value = process.env[envKey];
  // Se não existir a variável, assume true (compatibilidade com comportamento anterior)
  if (value === undefined) return true;
  // Converte string para boolean
  return value === 'true';
};

// Menus estáticos - visibilidade controlada via variáveis de ambiente
const MENUS = [
  {
    id: 'programacao',
    title: 'Programação',
    icon: 'calendar-clock',
    route: '/pages/programacao',
    submenus: [
      {
        id: 'programacaoHavan',
        title: 'Programação Havan',
        icon: 'calendar-clock',
        route: '/pages/programacao/havan',
        submenus: []
      },
      {
        id: 'emissaoProtocolo',
        title: 'Emissão de protocolo',
        icon: 'calendar-clock',
        route: '/pages/programacao/havan/protocolos',
        submenus: []
      }
    ]
  },
  {
    id: 'disponibilidade',
    title: 'Disponibilidade',
    icon: 'calendar-clock',
    route: '/pages/programacao/disponibilidade',
    submenus: []
  },
  {
    id: 'disponibilidadeFrotaPatio',
    title: 'Disponibilidade Frota Pátio',
    icon: 'calendar-clock',
    route: '/pages/programacao/disponibilidadeFrotaPatio',
    submenus: []
  },
  {
    id: 'clientes',
    title: 'Clientes',
    icon: 'domain',
    route: '/pages/clientes',
    requiredCognitoGroup: COGNITO_GROUP_CLIENTES,
    submenus: []
  },
  {
    id: 'operacoes',
    title: 'Operações',
    icon: 'cogs',
    route: '/pages/operacoes',
    requiredCognitoGroup: COGNITO_GROUP_OPERACOES,
    submenus: []
  },
  {
    id: 'viagens',
    title: 'Viagens',
    icon: 'map-marker-path',
    route: '/pages/viagens',
    requiredCognitoGroup: COGNITO_GROUP_VIAGENS,
    submenus: []
  },
  {
    id: 'pedidos',
    title: 'Pedidos',
    icon: 'cart-outline',
    route: '/pages/pedidos',
    requiredCognitoGroup: COGNITO_GROUP_PEDIDOS,
    submenus: [
      {
        id: 'pedidos',
        title: 'Pedidos',
        icon: 'cart-outline',
        route: '/pages/pedidos',
        submenus: []
      },
      {
        id: 'sugestaofrota',
        title: 'Planejamento detalhado',
        icon: 'cog',
        route: '/pages/planejamentodetalhado',
        submenus: []
      }
    ]
  },
  {
    id: 'veiculos',
    title: 'Veiculos',
    icon: 'truck-fast',
    route: '/pages/veiculos',
    requiredCognitoGroup: COGNITO_GROUP_VEICULOS,
    submenus: [
      {
        id: 'veiculos',
        title: 'Veiculos',
        icon: 'cart-outline',
        route: '/pages/veiculos',
        submenus: []
      },
      {
        id: 'modelos',
        title: 'Modelos',
        icon: 'cog',
        route: '/pages/modelos/modelos',
        submenus: []
      }
    ]
  },
  {
    id: 'container',
    title: 'Containers',
    icon: 'package-variant-closed',
    route: '/pages/containers',
    requiredCognitoGroup: COGNITO_GROUP_CONTAINERS,
    submenus: [
      {
        id: 'programacaoContainers',
        title: 'Programação',
        icon: 'cart-outline',
        route: '/pages/containers/programacao',
        submenus: []
      },
       {
        id: 'anexosContainers',
        title: 'Anexos',
        icon: 'cart-outline',
        route: '/pages/containers/anexos',
        submenus: []
      }
    ]
  },
  {
    id: 'deposito',
    title: 'Depósito',
    icon: 'warehouse',
    route: '/pages/deposito',
    requiredCognitoGroup: COGNITO_GROUP_DEPOSITO,
    submenus: []
  },
  {
    id: 'descarga',
    title: 'Descarga',
    icon: 'truck-delivery',
    route: '/pages/descarga',
    requiredCognitoGroup: COGNITO_GROUP_DESCARGA,
    submenus: [
      {
        id: 'descargaDashboard',
        title: 'Dashboard',
        icon: 'chart-box-outline',
        route: '/pages/descarga/dashboard',
        submenus: []
      },
      {
        id: 'descargaDescargas',
        title: 'Descargas',
        icon: 'truck-delivery',
        route: '/pages/descarga/descargas',
        submenus: []
      },
      {
        id: 'descargaJanela',
        title: 'Janela Descarga',
        icon: 'calendar-clock',
        route: '/pages/descarga/janela',
        submenus: []
      }
    ]
  },
  {
    id: 'cobrancasDeposito',
    title: 'Cobranças Depósito',
    icon: 'currency-usd',
    route: '/pages/cobrancas-deposito',
    requiredCognitoGroup: COGNITO_GROUP_DEPOSITO,
    submenus: []
  },
  {
    id: 'enderecamento',
    title: 'Enderecamento',
    icon: 'map-marker-radius',
    route: '/pages/enderecamento',
    requiredCognitoGroup: COGNITO_GROUP_DEPOSITO,
    submenus: []
  },
  {
    id: 'dashboard',
    title: 'Dashboards',
    icon: 'chart-box-outline',
    route: '/pages/dashboard',
    submenus: [
      {
        id: 'dashboardDeposito',
        title: 'Depósito',
        icon: 'warehouse',
        route: '/pages/dashboard/deposito',
        requiredCognitoGroup: COGNITO_GROUP_DEPOSITO,
        submenus: []
      },
      {
        id: 'dashboardPremiacao',
        title: 'Premiação',
        icon: 'trophy-outline',
        route: '/pages/dashboard/premiacao',
        requiredCognitoGroup: COGNITO_GROUP_BI_PREMIACAO,
        submenus: []
      },
      {
        id: 'dashboardMotoristas',
        title: 'Motoristas',
        icon: 'account-hard-hat',
        route: '/pages/dashboard/motoristas',
        submenus: []
      }
    ]
  },
  {
    id: 'acompanhamentos',
    title: 'Acompanhamentos',
    icon: 'monitor-dashboard',
    route: '/pages/acompanhamentos',
    requiredCognitoGroup: COGNITO_GROUP_ACOMPANHAMENTOS,
    submenus: []
  },
  {
    id: 'notificacoes',
    title: 'Notificações',
    icon: 'bell-outline',
    route: '/pages/notificacoes',
    requiredCognitoGroup: COGNITO_GROUP_NOTIFICACOES,
    submenus: []
  },
  {
    id: 'previsaoCargas',
    title: 'Previsão de Cargas',
    icon: 'truck-delivery',
    route: '/pages/previsao-cargas',
    requiredCognitoGroup: COGNITO_GROUP_PREVISAO_CARGAS,
    submenus: []
  },
  {
    id: 'motoristas',
    title: 'Motoristas',
    icon: 'account-hard-hat',
    route: '/pages/motoristas',
    requiredCognitoGroup: COGNITO_GROUP_MOTORISTAS,
    submenus: []
  }
];

const isMenuVisibleByCognito = (item, hasCognitoGroup) => {
  if (!item.requiredCognitoGroup) {
    return true;
  }

  return hasCognitoGroup(item.requiredCognitoGroup);
};

const isSubmenuVisible = (submenu, hasCognitoGroup) => {
  if (!isMenuEnabled(submenu.id)) {
    return false;
  }

  return isMenuVisibleByCognito(submenu, hasCognitoGroup);
};

const isMenuVisible = (menu, hasCognitoGroup) => {
  if (!isMenuEnabled(menu.id)) {
    return false;
  }

  return isMenuVisibleByCognito(menu, hasCognitoGroup);
};

const buildAvailableMenus = (hasCognitoGroup) => {
  return MENUS
    .filter(menu => isMenuVisible(menu, hasCognitoGroup))
    .map(menu => ({
      ...menu,
      submenus: menu.submenus.filter(submenu => isSubmenuVisible(submenu, hasCognitoGroup))
    }))
    .filter(menu => {
      const originalMenu = MENUS.find(item => item.id === menu.id);
      const hadSubmenus = Boolean(originalMenu && originalMenu.submenus.length > 0);

      if (hadSubmenus && menu.submenus.length === 0) {
        return false;
      }

      return true;
    });
};

const SidebarContent = ({
  onClick,
  collapse
}) => {
  const { hasCognitoGroup } = useAuth();
  const availableMenus = buildAvailableMenus(hasCognitoGroup);

  return (
    <SidebarContentWrap collapse={collapse}>
      <SidebarBlock collapse={collapse}>
        {availableMenus.map(menu => (
          <React.Fragment key={menu.id}>
            {menu.submenus.length > 0 ? (
              // Menu com submenus - usar SidebarCategory
              <SidebarCategory title={menu.title} icon={menu.icon} collapse={collapse}>
                {menu.submenus.map(submenu => (
                  <SidebarLink
                    key={submenu.id}
                    title={submenu.title}
                    route={submenu.route}
                    onClick={onClick}
                  />
                ))}
              </SidebarCategory>
            ) : (
              // Menu direto - usar SidebarLink
              <SidebarLink
                title={menu.title}
                route={menu.route}
                onClick={onClick}
                icon={menu.icon}
              />
            )}
          </React.Fragment>
        ))}
      </SidebarBlock>
    </SidebarContentWrap>
  );
};

SidebarContent.propTypes = {
  onClick: PropTypes.func.isRequired,
  collapse: PropTypes.bool
};

SidebarContent.defaultProps = {
  collapse: false
};

export default SidebarContent;

// region STYLES

const SidebarContentWrap = styled.div`
  height: 100%;
  overflow: auto;
  padding-top: 0;

  & > div:last-child {
    width: 4px !important;

    div {
      transition: height 0.3s ease;
      opacity: 0.52;
    }
  }

  @media screen and (min-width: 576px) {
    padding-top: 0;

    ${props => props.collapse && `
      width: 55px;
      overflow: visible !important;
      transition: width 0.3s ease;
    `}
  }
`;

const SidebarBlock = styled.ul`
  padding: 5px 0;
  border-bottom: 1px solid ${colorBorder};
  list-style-type: none;

  &:last-child {
    border: none;
  }

  @media screen and (min-width: 576px) {

    ${props => props.collapse && `
      & > li > a,
      & > li > button {
        overflow: hidden;
        width: 55px;
        background: ${colorBackground(props)};
        transition: width 0.3s ease;

        span:last-of-type {
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        ${SidebarLinkTitle} {
          position: absolute;
          width: 160px;
          ${left(props)}: 70px;
          transition: opacity 0.3s ease;
        }

        &:hover {
          background: ${colorHover(props)};
          width: 275px;

          span:last-of-type {
            opacity: 1;
          }

          ${SidebarLinkTitle} {
            opacity: 1;
          }
        }
      }
    `}
  }
`;

// endregion

